import React, { useState, useEffect, useRef } from "react";
import { Property } from "../../../../service";
import ButtonComponent from "../../../Ui/Button";
import Modal from "../../../Ui/Modal";
import { Formik, Form } from "formik";
import FormField from "../../VendorOnboarding/FormField";
import * as Yup from "yup";
import { tenantFieldConfig } from "../../../constant/Home/Properties";
import useApiHandler from "../../../hooks/ApiHandler";

const generateTenantId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tenant-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
};

const normalizeTenants = (tenantList = []) => {
  const list = Array.isArray(tenantList) && tenantList.length ? tenantList : [null];
  return list.map((tenant) => ({
    id: tenant?._internalId || tenant?.id || generateTenantId(),
    tenant_name: tenant?.tenant_name || "",
    unit: tenant?.unit || "",
    unitLabel: tenant?.unitLabel || tenant?.unit_label || tenant?.unit_apt || tenant?.unit || "",
    tenant_email: tenant?.tenant_email || "",
  }));
};

const AddTenantForm = ({
  setModalState,
  propertyId,
  setPropertyId,
  setTenantsInfo,
  tenantsInfo = [],
}) => {
  const formikSubmitRef = useRef(null);
  const currentFormValuesRef = useRef({});
  const { callApi, loading } = useApiHandler();

  const [propertyDetails, setPropertyDetails] = useState(null);

  const [tenants, setTenants] = useState(() => normalizeTenants(tenantsInfo));

  useEffect(() => {
    if (tenantsInfo?.length) {
      setTenants(normalizeTenants(tenantsInfo));
    } else if (tenants.length === 0) {
      setTenants(normalizeTenants());
    }
  }, [tenantsInfo, tenants.length]);

  // Generate dynamic unit options from propertyDetails
  const getDynamicUnitOptions = (excludeUnitId = null) => {
    if (!propertyDetails?.units || !Array.isArray(propertyDetails.units)) {
      return [];
    }

    return propertyDetails.units
      .filter((unit) => unit.status === "OPEN")
      .filter((unit) => unit.id.toString() !== excludeUnitId)
      .map((unit) => ({
        value: unit.id.toString(),
        label: unit.unit_apt || `Unit ${unit.id}`,
      }));
  };

  // Get assigned units from current form values
  const getAssignedUnits = (values) => {
    const assignedUnits = [];
    tenants.forEach((tenant) => {
      const unitValue = values[`tenant_${tenant.id}_unit`];
      if (unitValue && unitValue.trim() !== "") {
        assignedUnits.push(unitValue);
      }
    });
    return assignedUnits;
  };

  // Get available units for a specific tenant (excluding already assigned units)
  const getAvailableUnitsForTenant = (tenantId, values) => {
    const assignedUnits = getAssignedUnits(values);
    const currentTenantUnit = values[`tenant_${tenantId}_unit`];

    return getDynamicUnitOptions().filter((unit) => {
      // Include the current tenant's selected unit (if any)
      if (currentTenantUnit === unit.value) {
        return true;
      }
      // Exclude units that are assigned to other tenants
      return !assignedUnits.includes(unit.value);
    });
  };

  // Create dynamic field config with unit options
  const getDynamicTenantFieldConfig = () => {
    const unitOptions = getDynamicUnitOptions();

    return tenantFieldConfig.map((field) => {
      if (field.name === "unit") {
        return {
          ...field,
          options: unitOptions,
        };
      }
      return field;
    });
  };

  // validation schema
  const createValidationSchema = () => {
    const schemaFields = {};
    tenants.forEach((tenant, tenantIndex) => {
      getDynamicTenantFieldConfig().forEach((field) => {
        const fieldName = `tenant_${tenant.id}_${field.name}`;
        if (field.name === "tenant_name") {
          schemaFields[fieldName] = Yup.string()
            .trim()
            .required("Tenant name is required");
        } else if (field.name === "unit") {
          schemaFields[fieldName] = Yup.string()
            .required("Unit selection is required")
            .test(
              "unique-unit",
              "This unit is already assigned to another tenant",
              function (value) {
                if (!value) return true; // Let required validation handle empty values

                const { parent } = this;
                const assignedUnits = [];

                // Collect all assigned units except current field
                tenants.forEach((t) => {
                  const unitFieldName = `tenant_${t.id}_unit`;
                  if (unitFieldName !== fieldName && parent[unitFieldName]) {
                    assignedUnits.push(parent[unitFieldName]);
                  }
                });

                return !assignedUnits.includes(value);
              }
            );
        } else if (field.name === "tenant_email") {
          schemaFields[fieldName] = Yup.string()
            .email("Invalid email format")
            .trim()
            .required("Tenant email is required");
        }
      });
    });
    return Yup.object().shape(schemaFields);
  };

  useEffect(() => {
    if (propertyDetails && !tenantsInfo?.length) {
      setTenants(
        normalizeTenants([
          {
            tenant_name:
              typeof propertyDetails.tenant_name === "string"
                ? propertyDetails.tenant_name
                : "",
            unit:
              typeof propertyDetails.unit === "string"
                ? propertyDetails.unit
                : "",
            tenant_email:
              typeof propertyDetails.tenant_email === "string"
                ? propertyDetails.tenant_email
                : "",
          },
        ])
      );
    }
  }, [propertyDetails, tenantsInfo?.length]);

  useEffect(() => {
    if (propertyId) {
      callApi(() => Property.getPropertyById(propertyId), {
        onSuccess: (response) => {
          if (response.status === 200) {
            setPropertyDetails(response.data);
          }
        },
        errorMessage: "Failed to fetch property details",
      });
    }
  }, [propertyId]);

  const addTenant = (setFieldValue) => {
    const newId = generateTenantId();

    // Store current form values before adding new tenant
    const currentValues = { ...currentFormValuesRef.current };

    setTenants((prev) => [
      ...prev,
      { id: newId, tenant_name: "", unit: "", unitLabel: "", tenant_email: "" },
    ]);

    // Initialize new fields in Formik
    getDynamicTenantFieldConfig().forEach((field) => {
      setFieldValue(`tenant_${newId}_${field.name}`, "");
    });

    // Restore previous form values
    Object.keys(currentValues).forEach((fieldName) => {
      if (currentValues[fieldName] !== undefined) {
        setFieldValue(fieldName, currentValues[fieldName]);
      }
    });
  };

  const removeTenant = (id) => {
    if (tenants.length > 1) {
      setTenants((prev) => prev.filter((tenant) => tenant.id !== id));
    }
  };

  const formatUnitLabel = (unitId) => {
    if (!unitId || !propertyDetails?.units) return unitId;
    const unitMatch = propertyDetails.units.find(
      (unit) => unit.id?.toString() === unitId?.toString()
    );
    if (!unitMatch) return unitId;
    if (unitMatch.unit_apt) return unitMatch.unit_apt;
    if (unitMatch.unit_label) return unitMatch.unit_label;
    if (propertyDetails?.property_type === "Commercial Building") {
      return `Commercial Unit ${unitMatch.id}`;
    }
    if (propertyDetails?.property_type === "Apartment") {
      return `Apartment ${unitMatch.id}`;
    }
    return `Unit ${unitMatch.id}`;
  };

  const handleSubmit = (values) => {
    const extractedTenants = tenants.map((tenant) => {
      const unitId = values[`tenant_${tenant.id}_unit`] || "";
      const unitLabel = formatUnitLabel(unitId) || "";
      return {
        tenant_name: values[`tenant_${tenant.id}_tenant_name`] || "",
        unit: unitId,
        unitLabel,
        tenant_email: values[`tenant_${tenant.id}_tenant_email`] || "",
        _internalId: tenant.id,
      };
    });

    setTenantsInfo(extractedTenants);
    setModalState("tenantsInvitation");
  };

  const handleClose = () => {
    setPropertyId(null);
    setModalState("");
  };

  const getInitialValues = () => {
    const values = {};
    tenants.forEach((tenant) => {
      getDynamicTenantFieldConfig().forEach((field) => {
        const value = tenant[field.name];
        values[`tenant_${tenant.id}_${field.name}`] =
          typeof value === "string" ? value : "";
      });
    });
    return values;
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      title="Add Tenants"
      footer={
        <div className="flex w-full gap-4 justify-end">
          <ButtonComponent paddingY="10px" onClick={() => handleClose()}>
            Cancel
          </ButtonComponent>
          <ButtonComponent
            bgColor="#3CAB5C"
            color="#FFFFFF"
            paddingY="10px"
            onClick={() => formikSubmitRef.current && formikSubmitRef.current()}
          >
            Save
          </ButtonComponent>
        </div>
      }
    >
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize={false}
        validationSchema={createValidationSchema()}
        onSubmit={handleSubmit}
      >
        {({
          setFieldValue,
          submitForm,
          errors,
          touched,
          values,
          setFieldTouched,
        }) => {
          formikSubmitRef.current = submitForm;
          currentFormValuesRef.current = values;

          // Watch for unit changes and trigger validationedit
          useEffect(() => {
            // Trigger validation for all unit fields when values change
            Object.keys(values).forEach((key) => {
              if (key.includes("_unit") && values[key]) {
                setFieldTouched(key, true);
              }
            });
          }, [values]);

          return (
            <Form>
              <div>
                {tenants.map((tenant) => (
                  <div key={tenant.id} className="md:p-4 p-2">
                    <div className="flex justify-between items-center mb-4">
                      {tenants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTenant(tenant.id)}
                          className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 [&>*:nth-child(n+3)]:md:col-span-2">
                      {getDynamicTenantFieldConfig().map(
                        (field, fieldIndex) => {
                          // Get filtered options for unit field
                          const fieldOptions =
                            field.name === "unit"
                              ? getAvailableUnitsForTenant(tenant.id, values)
                              : field.options;

                          return (
                            <div key={fieldIndex}>
                              <FormField
                                label={field.label}
                                name={`tenant_${tenant.id}_${field.name}`}
                                type={field.type}
                                placeholder={field.placeholder}
                                options={fieldOptions}
                                error={
                                  errors[`tenant_${tenant.id}_${field.name}`]
                                }
                                touched={
                                  touched[`tenant_${tenant.id}_${field.name}`]
                                }
                              />
                              {field.name === "unit" &&
                                fieldOptions.length === 0 && (
                                  <p className="text-sm text-red-600 mt-1">
                                    No available units. All units are already
                                    assigned to other tenants.
                                  </p>
                                )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex text-nowrap justify-center mb-4">
                  <ButtonComponent
                    color="#52C171"
                    bgColor="#52C1711A"
                    borderColor="#3CAB5C"
                    paddingY="6px"
                    type="button"
                    onClick={() => addTenant(setFieldValue)}
                  >
                    Add Additional Tenant
                  </ButtonComponent>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddTenantForm;
